/**
 * Generic AI insight endpoint — works for any tool that has a structured
 * "what we computed" + a user prompt.
 *
 * POST /api/ai/insight
 *   body: { topic: string, context: object, prompt?: string }
 *
 * Topics:
 *   - "dream"       — context = { keyword, meaning, lottery? }
 *   - "bat-tu"      — context = { pillars, fiveElements, dayMaster }
 *   - "i-ching"     — context = { original, changed?, lines }
 *   - "horoscope"   — context = { zodiac, date, score, ... }
 *   - "numerology"  — context = { lifePath, expression, soul, name }
 *
 * Returns SSE stream like /api/ai/chat. Auth not required for now —
 * Phase 3 will add per-user rate limiting.
 *
 * Why a separate endpoint vs reusing /api/ai/chat: chat builds a Tử-Vi
 * chart and feeds it into the prompt. These tools have their own
 * context shapes, so we keep the prompt builder + provider call
 * generic here.
 */
import { NextRequest } from "next/server";
import { getProvider } from "@/lib/ai/provider";

export const runtime = "nodejs";

type Body = {
  topic?: string;
  context?: Record<string, unknown>;
  prompt?: string;
};

const MAX_PROMPT = 800;
const ALLOWED_TOPICS = new Set([
  "dream",
  "bat-tu",
  "i-ching",
  "horoscope",
  "numerology",
  "compat-method",
]);

function sseEvent(payload: Record<string, unknown>): string {
  return `data: ${JSON.stringify(payload)}\n\n`;
}

function systemPromptFor(topic: string, ctx: Record<string, unknown>): string {
  const base =
    "Bạn là chuyên gia tử vi, phong thủy, và thần số học người Việt. " +
    "Trả lời ngắn gọn (200-400 chữ), chia thành 2-3 đoạn rõ ràng. " +
    "Dùng ngôn ngữ Vietnamese trang trọng nhưng dễ hiểu, KHÔNG dùng markdown headers. " +
    "Nhấn mạnh đây là tham khảo văn hoá, không thay thế lời khuyên chuyên môn về y tế / pháp luật / tài chính. ";

  switch (topic) {
    case "dream":
      return (
        base +
        `Người dùng vừa tra một giấc mơ. Dữ liệu giấc mơ: ${JSON.stringify(ctx)}. ` +
        "Phân tích sâu hơn ý nghĩa giấc mơ này theo cả góc nhìn folklore Việt Nam và tâm lý hiện đại. " +
        "Gợi ý điều người mơ có thể đang lo lắng hoặc khao khát."
      );
    case "bat-tu":
      return (
        base +
        `Người dùng vừa lập Bát Tự. Dữ liệu 4 trụ: ${JSON.stringify(ctx)}. ` +
        "Luận sơ bộ ngũ hành cân bằng, nhật-chủ vượng/suy, và 1-2 lời khuyên về sự nghiệp / sức khoẻ phù hợp. " +
        "Không phán mệnh tuyệt đối."
      );
    case "i-ching":
      return (
        base +
        `Người dùng vừa gieo Kinh Dịch. Dữ liệu quẻ: ${JSON.stringify(ctx)}. ` +
        "Diễn giải ý nghĩa quẻ chính, hào động (nếu có), và quẻ biến trong ngữ cảnh đời sống hiện đại. " +
        "Đưa ra 1-2 hành động cụ thể người gieo nên cân nhắc."
      );
    case "horoscope":
      return (
        base +
        `Người dùng vừa xem tử vi. Dữ liệu: ${JSON.stringify(ctx)}. ` +
        "Mở rộng phần luận giải tổng quan thành nội dung chi tiết hơn, giữ tinh thần lạc quan nhưng thực tế. " +
        "Gợi ý 2-3 việc nên làm và 1 việc nên tránh trong khoảng thời gian này."
      );
    case "numerology":
      return (
        base +
        `Người dùng vừa tính thần số học. Dữ liệu: ${JSON.stringify(ctx)}. ` +
        "Phân tích sự cộng hưởng giữa Số chủ đạo, Số biểu cảm và Số linh hồn. " +
        "Chỉ ra điểm mạnh tiềm ẩn và bài học cuộc đời mà tổ hợp số này gợi ý."
      );
    case "compat-method": {
      // ctx must include: methodKey, methodLabel, score, summary, details, self, partner
      const methodKey = String(ctx.methodKey ?? "");
      const methodLabel = String(ctx.methodLabel ?? "phương pháp");
      const longBase =
        "Bạn là chuyên gia tử vi, phong thủy và quan hệ người Việt. " +
        "Trả lời dài và chi tiết (450-650 chữ), chia thành 3-4 đoạn rõ ràng. " +
        "Dùng tiếng Việt trang trọng nhưng dễ hiểu, KHÔNG dùng markdown headers. " +
        "Trình bày theo cấu trúc: (1) Phân tích sâu cách hệ thống này nhìn cặp đôi, " +
        "(2) Chỉ ra điểm mạnh CỤ THỂ với tên người và ngày sinh, " +
        "(3) Chỉ ra rủi ro CỤ THỂ và cách hoá giải, " +
        "(4) Lời khuyên hành động cho 6 tháng tới. " +
        "Nhấn mạnh đây là tham khảo, không phán quyết tuyệt đối. ";

      const variantPrompt: Record<string, string> = {
        traditional:
          `Người dùng đang xem kết quả Tử Vi truyền thống Việt Nam (4 yếu tố Thiên Can - Địa Chi - Ngũ Hành Nạp Âm - Cung Phi Bát Trạch). ` +
          `Dữ liệu: ${JSON.stringify(ctx)}. ` +
          "Luận sâu từng yếu tố trong 4 sub-factors, giải thích chữ Hán nếu có, dẫn ví dụ về tam hợp/lục hợp/tứ hành xung và sinh-khắc ngũ hành. " +
          "Đối chiếu với kinh nghiệm dân gian Việt về cặp đôi cùng Cung Phi (Đông/Tây Tứ Mệnh) ảnh hưởng đến hướng nhà chung. " +
          "Gợi ý cụ thể về ngày tốt cưới hỏi và phong thuỷ phòng cưới phù hợp với 4 yếu tố này.",
        western:
          `Người dùng đang xem kết quả Cung Hoàng Đạo phương Tây (12 cung × 4 nguyên tố × 3 modality). ` +
          `Dữ liệu: ${JSON.stringify(ctx)}. ` +
          "Luận sâu cách 2 cung phương Tây tương tác qua Element (Lửa/Đất/Khí/Nước) và Modality (Khởi đầu/Cố định/Linh hoạt). " +
          "Đưa ra phân tích cảm xúc và giao tiếp dựa trên đặc trưng tâm lý phương Tây của 2 cung. " +
          "Liên hệ với kinh điển astrology như synastry và composite chart (giải thích đơn giản). " +
          "Gợi ý 3 hoạt động cụ thể giúp 2 cung này hoà hợp hơn (ví dụ: cách giao tiếp, phong cách hẹn hò, cách giải quyết xung đột).",
        "bat-tu":
          `Người dùng đang xem kết quả Bát Tự nhật chủ — phương pháp xem lá số 4 trụ cao cấp nhất. ` +
          `Dữ liệu: ${JSON.stringify(ctx)}. ` +
          "Luận sâu mối quan hệ giữa 2 Day Master (nhật-can): sinh, khắc, tỉ hoà, tiết khí. " +
          "Giải thích vai trò ngũ hành Dụng thần và Kỵ thần của từng người trong cặp đôi. " +
          "Phân tích xem ai là người 'cho' năng lượng, ai là người 'nhận' trong cặp này. " +
          "Đưa ra gợi ý phong thuỷ NHÀ Ở chung (hướng, màu sơn) sao cho dụng thần của cả hai đều được hỗ trợ. " +
          "Cảnh báo nếu có mâu thuẫn dụng thần (ví dụ vợ cần Hỏa nhưng chồng cần Thuỷ).",
        numerology:
          `Người dùng đang xem kết quả Thần Số Học Life Path (hệ Pythagore). ` +
          `Dữ liệu: ${JSON.stringify(ctx)}. ` +
          "Luận sâu ý nghĩa của 2 Life Path number kết hợp với nhau. Mô tả: (a) cách 2 con số này cộng hưởng hoặc xung đột, (b) bài học chung mà cặp đôi cần học (life lesson) thông qua mối quan hệ này, (c) chu kỳ 9 năm sẽ ảnh hưởng đến mối quan hệ ra sao. " +
          "Trích dẫn các nhà thần số học nổi tiếng (Dan Millman, Hans Decoz) khi diễn giải. " +
          "Gợi ý ngày kết hôn tốt theo personal year cycle.",
      };

      return (
        longBase +
        (variantPrompt[methodKey] ??
          `Phương pháp: ${methodLabel}. Dữ liệu: ${JSON.stringify(ctx)}. Phân tích sâu phương pháp này áp dụng vào cặp đôi.`)
      );
    }
    default:
      return base;
  }
}

export async function POST(req: NextRequest) {
  let body: Body;
  try {
    body = (await req.json()) as Body;
  } catch {
    return new Response(JSON.stringify({ error: "Invalid JSON body" }), {
      status: 400,
      headers: { "Content-Type": "application/json" },
    });
  }

  const { topic, context, prompt } = body;
  if (!topic || !ALLOWED_TOPICS.has(topic)) {
    return new Response(JSON.stringify({ error: "Topic không hợp lệ." }), {
      status: 400,
      headers: { "Content-Type": "application/json" },
    });
  }
  if (!context || typeof context !== "object") {
    return new Response(JSON.stringify({ error: "Thiếu context dữ liệu." }), {
      status: 400,
      headers: { "Content-Type": "application/json" },
    });
  }

  const userPrompt = (prompt ?? "Phân tích giúp tôi.").slice(0, MAX_PROMPT);
  const sys = systemPromptFor(topic, context);

  const provider = await getProvider();
  const encoder = new TextEncoder();

  const stream = new ReadableStream<Uint8Array>({
    async start(controller) {
      controller.enqueue(encoder.encode(sseEvent({ provider: provider.name })));
      try {
        // Use the lower-level streamMessages API — these tools have their
        // own context shape (dream / bat-tu / i-ching / horoscope / numerology)
        // and don't need the Tử Vi chart prompt builder.
        for await (const chunk of provider.streamMessages([
          { role: "system", content: sys },
          { role: "user", content: userPrompt },
        ])) {
          controller.enqueue(encoder.encode(sseEvent({ delta: chunk })));
        }
        controller.enqueue(encoder.encode(sseEvent({ done: true })));
      } catch (e) {
        const msg = e instanceof Error ? e.message : "AI provider failed";
        controller.enqueue(encoder.encode(sseEvent({ error: msg })));
      } finally {
        controller.close();
      }
    },
  });

  return new Response(stream, {
    headers: {
      "Content-Type": "text/event-stream; charset=utf-8",
      "Cache-Control": "no-cache, no-transform",
      Connection: "keep-alive",
      "X-Accel-Buffering": "no",
    },
  });
}
